import { hash } from 'immutable';
import type { UnknownAction } from 'redux';

import { resourceUndoUpserters, resourceUpserters } from 'app-components/flux/core';
import type { QcsSyncResourceStore } from 'app-components/flux/core/resourceStoreTypes';
import { coreActions } from 'app-components/flux/core-actions';
import { generatedReportsActions } from 'app-components/flux/generated-reports/generatedReportsSlice';
import { postponedActionsActions } from 'app-components/flux/postponed-actions';
import {
  deleteScheduledTransaction,
  deleteScheduledTransactionFailure,
  deleteScheduledTransactionSuccess,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsActions';
import { getQcsDeletedResourceId } from 'app-components/utils/core/httpErrors';
import { hasAsyncDispatch } from 'app-components/utils/redux-store/asyncDispatchMiddleware';

import { getDeletedAccountIds } from '../accounts/accountsUtils';
import * as actions from './transactionsActions';
import * as transactionsTypes from './transactionsTypes';

/** Transaction writes whose optimistic update needs undoing if the request is queued for retry. */
const postponedTransactionWriteTypes = new Set<string>([
  actions.batchTransactions.type,
  actions.createTransaction.type,
  actions.updateTransaction.type,
  actions.deleteTransaction.type,
]);

// addPostponedAction is declared without a payload type, so the shape is asserted here rather than
// at each use.
type PostponedActionPayload = { action?: UnknownAction; error?: unknown };

const isPostponedTransactionWrite = (payload: unknown) => {
  const postponedAction = (payload as PostponedActionPayload)?.action;
  return !!postponedAction && postponedTransactionWriteTypes.has(postponedAction.type);
};

/** QCS drops null and absent fields from a write, so Personal has to be named explicitly. */
const PERSONAL_USAGE_ID = '';

const explicitUsageFields = (fields: {
  businessId?: string | null;
  customerId?: string | null;
  projectId?: string | null;
}) => ({
  businessId: fields.businessId ?? PERSONAL_USAGE_ID,
  customerId: fields.customerId ?? '',
  projectId: fields.projectId ?? '',
});

/** Restates usage on a transaction and on its split lines, which carry their own. */
const withExplicitUsage = (txn: transactionsTypes.TransactionRecord): transactionsTypes.TransactionRecord => {
  const items = txn.split?.items;
  const restated = txn.merge(explicitUsageFields(txn));

  return items
    ? restated.setIn(
        ['split', 'items'],
        transactionsTypes.mkSplitItems(items).map((item) => item.merge(explicitUsageFields(item))),
      )
    : restated;
};

const undoUpdateTransaction = (...[txn, meta]: Parameters<typeof actions.updateTransaction>) =>
  actions.updateTransaction(withExplicitUsage(txn), meta);

const undoBatchTransactions = (...[txns, meta]: Parameters<typeof actions.batchTransactions>) =>
  actions.batchTransactions(txns.map(withExplicitUsage), meta);

function reducer(
  state: QcsSyncResourceStore<
    transactionsTypes.TransactionProps,
    transactionsTypes.TransactionStoreProps
  > = transactionsTypes.mkTransactionStore(),
  action: UnknownAction,
): typeof state {
  // TODO: Some of these `asyncDispatch`s are going to be gnarly to unwind and move to a saga so I am leaving this hack in for now
  const asyncDispatch = hasAsyncDispatch(action) ? action.asyncDispatch : undefined;
  switch (true) {
    case coreActions.sanitize.match(action):
      return state.set(
        'resourcesById',
        state.resourcesById.map((resource) =>
          resource.mergeDeep({
            payee: hash(resource.payee).toString(),
            memo: hash(resource.memo).toString(),
            ...(resource.check && {
              check: {
                memo: hash(resource.check.memo).toString(),
              },
            }),
            ...(resource.cpData && {
              cpData: {
                payee: hash(resource.cpData.payee).toString(),
                inferredPayee: hash(resource.cpData.inferredPayee).toString(),
                memo: hash(resource.cpData.memo).toString(),
              },
            }),
          }),
        ),
      );
    case actions.getTransactions.match(action):
      return state.set('isLoading', true);
    case actions.getTransactionsSuccess.match(action): {
      const dateOnAfter = action.meta?.config?.axiosConfig?.params?.dateOnAfter;
      const dateOnBefore = action.meta?.config?.axiosConfig?.params?.dateOnBefore;
      const goalAccountIds = action.meta?.props?.goalAccountIds;
      const newState = resourceUpserters.upsertResources(
        state,
        'set' in action.payload ? action.payload.set('doMerge', false) : { ...action.payload, doMerge: false },
      );
      let lastSyncDate = newState.lastSyncDate;
      if (dateOnBefore) {
        lastSyncDate = state.lastSyncDate; // do not update if specific chunk loaded
      } else if (action.meta?.props?.incremental && state.lastSyncDate === null) {
        // A failed or queued write reset the sync while this fetch was in flight. Taking its asOf
        // would make the reconcile fetch incremental too, and that skips the rows the reset is for.
        lastSyncDate = null;
      }
      const resourcesById = newState.resourcesById.filter(
        (transaction) =>
          !dateOnAfter ||
          (transaction.postedOn ?? '') > dateOnAfter || // apply txn date filter
          (transaction.accountId && goalAccountIds?.has(transaction.accountId)), // dont remove goal-side txns
      );
      return newState.merge({
        resourcesById: resourcesById.size === newState.resourcesById.size ? newState.resourcesById : resourcesById,
        dateOnAfter,
        lastSyncDate,
      });
    }
    case actions.getGoalTransactionsSuccess.match(action):
      // Goal refreshes re-fetch every goal-account row. Keep value-equal records so downstream selector
      // caches survive, and keep the global sync cursor, which this scoped load must not advance.
      return resourceUpserters
        .upsertResources(state, resourceUpserters.dropUnchangedResources(state, action.payload))
        .set('lastSyncDate', state.lastSyncDate);
    case actions.getTransactionSuccess.match(action):
      // A targeted load may intentionally fetch a transaction outside dateOnAfter. Keep the
      // current collection and history-window metadata while making that transaction available.
      return resourceUpserters.upsertResources(state, action.payload);

    case actions.getTransactionsFailure.match(action):
    case actions.getGoalTransactionsFailure.match(action):
      return resourceUpserters.completeWithError(state, action.payload);

    case actions.createTransaction.match(action):
      return resourceUpserters.upsertResource(state, action.payload);
    case actions.createTransactionSuccess.match(action):
      return resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.deleteTransaction);

    case actions.updateTransaction.match(action):
      return resourceUndoUpserters.upsertResourceSetupUndo(state, action, undoUpdateTransaction);
    case actions.updateTransactionSuccess.match(action):
      return resourceUpserters.upsertResource(state, action.payload, undefined, undefined, 'default');

    case actions.deleteTransaction.match(action):
      return resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.createTransaction);
    case actions.deleteTransactionSuccess.match(action):
      return resourceUpserters.upsertResource(state, action.payload);

    case actions.batchTransactions.match(action):
      return resourceUndoUpserters.upsertResourcesSetupUndo(
        state,
        action,
        undoBatchTransactions,
        false,
        undefined,
        action.meta?.skipOptimistic,
      );
    case actions.batchTransactionsSuccess.match(action):
      return resourceUndoUpserters.upsertResourcesSetupUndo(state, action, actions.batchTransactions, true);

    case actions.applyAccountsChangesClone.match(action): {
      // accounts cascade deletion
      const deletedAccountIds = getDeletedAccountIds(action.payload);
      if (deletedAccountIds.length) {
        asyncDispatch?.(actions.getTransactions(undefined, { context: 'applyAccountsChanges' })); /// TODO: remove after bump is enabled
        const resourcesById = state.resourcesById.filter(
          (transaction) => !deletedAccountIds.includes(transaction.accountId || ''),
        );
        return state.merge({ resourcesById });
      }
      return state;
    }

    case deleteScheduledTransaction.match(action):
    case deleteScheduledTransactionSuccess.match(action):
      // scheduled trandsactions cascade deletion
      return state.merge({
        resourcesById: state.resourcesById.filter(
          (transaction) =>
            transaction.stModelId !== action.payload.id ||
            transaction.source !== 'SCHEDULED_TRANSACTION_PENDING' ||
            transaction.state !== 'PENDING' ||
            transaction.matchState !== 'NOT_MATCHED',
        ),
      });

    case actions.performTransactionAction.match(action):
      return state.set('isLoading', true);
    case actions.performTransactionActionSuccess.match(action):
      return resourceUpserters.upsertResources(
        state,
        { resources: Array.isArray(action.payload) ? action.payload : [action.payload] },
        'default',
      );

    case actions.applyTransactionsChanges.match(action):
      return resourceUpserters.upsertResources(state, { resources: action.payload, doMerge: false }, 'default');
    case generatedReportsActions.reconcileReportTransactionsSuccess.match(action):
      return resourceUpserters
        .upsertResources(state, { resources: action.payload.resources }, 'default')
        .set('lastSyncDate', state.lastSyncDate);
    case actions.getTransactionsEarliestDateOnSucess.match(action):
      return state.merge({ earliestDateOn: action.payload.dateOn });

    case actions.initiateA2ATransferSuccess.match(action):
      return resourceUpserters.upsertResources(state, { resources: action.payload });
    case actions.initiateA2ATransferFailure.match(action):
      return resourceUpserters.completeWithError(state, action.payload);

    case actions.createTransactionFailure.match(action):
    case actions.updateTransactionFailure.match(action):
    case actions.deleteTransactionFailure.match(action):
    case actions.batchTransactionsFailure.match(action):
    case actions.performTransactionActionFailure.match(action):
    case deleteScheduledTransactionFailure.match(action): {
      asyncDispatch?.(actions.getTransactions(undefined, { context: actions.RECONCILE_WRITE_CONTEXT }));
      const resetState = resourceUpserters.resetResources(state, action.payload);
      // QCS says the row is already deleted (QCS-0400-5). No sync will remove it: an incremental one
      // may never report the delete, and a full one only merges the rows it returns. Drop it here,
      // or the user can act on it again and hit the same error every time.
      const deletedId = getQcsDeletedResourceId(action.payload);
      const deletedTxn = deletedId ? resetState.resourcesById.get(deletedId) : undefined;
      return deletedTxn
        ? resourceUpserters.upsertResources(resetState, {
            resources: [deletedTxn.set('isDeleted', true)],
            error: action.payload,
          })
        : resetState;
    }

    // A write queued for retry has not been reconciled by anything: no failure action fires, so the
    // optimistic update stands on rows the server never wrote. Nothing replays the queue on its own
    // either, so it is reconciled here the same way an outright failure is. Clearing lastSyncDate is
    // the load-bearing half — without it the next sync is incremental, and rows the server never
    // touched are absent from it, so the wrong values survive indefinitely.
    case action.type === postponedActionsActions.addPostponedAction.toString() &&
      isPostponedTransactionWrite(action.payload): {
      asyncDispatch?.(actions.getTransactions(undefined, { context: actions.RECONCILE_WRITE_CONTEXT }));
      return resourceUpserters.resetResources(state, (action.payload as PostponedActionPayload).error);
    }

    default:
      return state;
  }
}

export default reducer;
